import classNames from 'classnames';
import Loader from 'components/Loader';
import {
  RecentActivity,
  RecentActivityBottom,
  RecentActivityClickable,
} from 'components/RecentActivity';
import { has_permission } from 'config/permissions';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';
import { FC, ReactNode, useEffect, useState } from 'react';
import { DonorDetailTabs } from 'views/DonationDetail/donorDetailTabs';
import { IDonorTotal } from 'services/donors/types';
import { IUserDates } from 'store/viewPreferences/types';

interface TabConfig {
  label: string;
  view: ReactNode;
}

interface DonationDetailContentProps {
  activeRole: string | null;
  donors?: IDonorTotal[];
  donorsState: { isLoading: boolean };
  permissionKey: string;
  screen: DashboardScreen;
  sessionStorageKey: string;
  tabs: TabConfig[];
  userClientId?: number | null;
  userDates: IUserDates | null;
}

const DonationDetailContent: FC<DonationDetailContentProps> = ({
  activeRole,
  donors = [],
  donorsState,
  permissionKey,
  screen,
  sessionStorageKey,
  tabs,
  userClientId,
  userDates,
}) => {
  const [recentActivityMini, setRecentActivityMini] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const savedIndex = sessionStorage.getItem(sessionStorageKey);

    if (savedIndex !== null) {
      const parsedIndex = parseInt(savedIndex, 10);
      if (!Number.isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < tabs.length) {
        setActiveIndex(parsedIndex);
        return;
      }
    }

    if (tabs.length === 0) {
      setActiveIndex(0);
      return;
    }

    setActiveIndex(prev => (prev < tabs.length ? prev : 0));
  }, [sessionStorageKey, tabs.length, userClientId, userDates?.start, userDates?.end]);

  useEffect(() => {
    if (activeIndex >= tabs.length && tabs.length > 0) {
      setActiveIndex(0);
    }
  }, [activeIndex, tabs.length]);

  const handleTabChange = (index: number) => {
    if (index !== activeIndex) {
      trackEvent('Donation Detail Tab Changed', { screen, tab: tabs[index]?.label ?? '' });
    }
    setActiveIndex(index);
    sessionStorage.setItem(sessionStorageKey, index.toString());
  };

  return (
    <>
      {donorsState.isLoading && <Loader type="dots" />}
      {!has_permission(permissionKey, 'R', activeRole) ? (
        <div className="content">
          <h1 className="title-permission">
            Sorry, you don&apos;t have permission to see this screen.
          </h1>
        </div>
      ) : (
        <>
          <div className="row filters-row">
            <DonorDetailTabs
              labels={tabs}
              activeTabIndex={activeIndex}
              onTabChange={handleTabChange}
            />

            <RecentActivityClickable
              isMini={recentActivityMini}
              setIsMini={setRecentActivityMini}
              screen={screen}
            />
          </div>
          <div className={classNames('content', { 'content-mini': recentActivityMini })}>
            <RecentActivity
              isMini={recentActivityMini}
              donors={donors}
              isLoading={donorsState.isLoading}
            />
            {tabs[activeIndex]?.view}
            <RecentActivityBottom
              isMini={recentActivityMini}
              setIsMini={setRecentActivityMini}
              donors={donors}
              isLoading={donorsState.isLoading}
              screen={screen}
            />
          </div>
        </>
      )}
    </>
  );
};

export default DonationDetailContent;
